import { Patterns } from '../../shared/validation/patterns';
import { IValidator, Validator } from '../../shared/validation/validators';
import { ValidationUtils } from '../../shared/validation/validation-runner';
import { JobseekerService } from '../shared/services/jobseeker.service';
import { FieldState } from '../../shared/models/field-state';
import { Utils } from '../../shared/utils';
import { Gender } from './gender-enum';
import { DrivingLicense } from '../../shared/models/driving-license.model';
import { CommonLookupItem } from '../../shared/models/common-lookup-item.model';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { JobseekerModel } from '@admin/jobseekers/shared/models/jobseeker-model';
import { ConfigurationService } from '../../shared/services/configuration.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'hr-jobseeker-details',
  templateUrl: './jobseeker-details.component.html',
  styleUrls: ['./jobseeker-details.component.less'],
  standalone: false,
})
export class JobseekerDetailsComponent implements OnInit, OnChanges {
  public extendedLocationPath = new FormControl(null);

  @Output()
  public onUpdate: EventEmitter<JobseekerModel> =
    new EventEmitter<JobseekerModel>();

  @Input('jobseeker')
  public jobseekerInput: JobseekerModel;

  @Input()
  public drivingLicensesInput: CommonLookupItem<number, string>[];

  @Input()
  public localities: CommonLookupItem<number, string>[];

  public dayList: number[];
  @Input()
  public monthList: CommonLookupItem<number, string>[];
  @Input()
  public yearList: CommonLookupItem<number, string>[];
  @Input()
  public genderList: CommonLookupItem<number, string>[];

  @Input()
  public locationLevelCount: number;
  @Input()
  public locationFirstLevelId: string;
  @Input()
  public locationRootId: string;
  @Input()
  public jobseekerLocationRequiredLevelCount: number;
  @Input()
  public jobseekerLocationLastHiddenLevelCount: number;

  public jobseeker: JobseekerModel;

  public isInEditMode: Boolean = false;

  public fieldStates: {
    genderId: FieldState;
    birthDate: FieldState;
    birthDay: FieldState;
    birthMonth: FieldState;
    birthYear: FieldState;
    city: FieldState;
    address: FieldState;
    drivingLicenses: FieldState;
  };

  public get field() {
    return this.fieldStates;
  }

  public gender: Gender;
  public Gender: any = Gender;

  public genderOriginalState: { genderId: number };
  public birthDateOriginalState: { day: string; month: string; year: string };
  public cityOriginalState: { cityId: number; cityName: string };
  public addressOriginalState: { address: string };
  public drivingLicenseOriginalState: { drivingLicenses: DrivingLicense[] };

  public drivingLicense: CommonLookupItem<number, string>;
  public isAllDrivingLicensesSelected: boolean;

  public birthDayClassName: string;

  public dateFormat: string;

  constructor(
    private jobseekerService: JobseekerService,
    private translationService: TranslateService,
    private config: ConfigurationService
  ) {
    this.jobseeker = new JobseekerModel();
    this.dayList = [];

    for (let i = 1; i <= 31; i++) this.dayList.push(i);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jobseekerInput'].currentValue != null) {
      this.jobseeker = Utils.cloneObject(this.jobseekerInput, JobseekerModel);
      if (this.jobseeker && this.jobseeker.genderId == null)
        this.jobseeker.genderId = 0;
      this.initializeFieldsState();
    }
  }

  public initializeFieldsState() {
    this.fieldStates = {
      genderId: new FieldState(
        () => this.jobseeker && this.jobseeker.genderId,
        () => {
          return this.jobseeker && this.jobseeker.genderId > 0;
        }
      ),
      birthDate: new FieldState(
        () => this.jobseeker && this.jobseeker.birthDate
      ),
      birthDay: new FieldState(() => this.jobseeker && this.jobseeker.birthDay),
      birthMonth: new FieldState(
        () => this.jobseeker && this.jobseeker.birthMonth
      ),
      birthYear: new FieldState(
        () => this.jobseeker && this.jobseeker.birthYear
      ),
      city: new FieldState(() => this.jobseeker && this.jobseeker.cityName),
      address: new FieldState(() => this.jobseeker && this.jobseeker.address),
      drivingLicenses: new FieldState(
        () => this.jobseeker && this.jobseeker.drivingLicenses,
        () => {
          return (
            this.jobseeker &&
            this.jobseeker.drivingLicenses &&
            this.jobseeker.drivingLicenses.length > 0
          );
        }
      ),
    };
  }

  private initialize() {
    this.initializeFieldsState();
    this.isInEditMode = false;
    this.isAllDrivingLicensesSelected
      ? this.jobseeker &&
        this.jobseeker.drivingLicenses &&
        this.jobseeker.drivingLicenses.length ==
          this.drivingLicensesInput.length
      : false;
  }

  private saveGenderOriginalState() {
    this.genderOriginalState = {
      genderId: this.jobseeker && this.jobseeker.genderId,
    };
  }

  private restoreGenderOriginalState() {
    this.jobseeker.genderId = this.genderOriginalState.genderId;
  }

  public onGenderEditClick() {
    this.saveGenderOriginalState();
    this.isInEditMode = true;
    this.fieldStates.genderId.isInEditMode = true;
  }

  public onGenderEditCancelClick() {
    this.fieldStates.genderId.isInEditMode = false;
    this.isInEditMode = false;
    this.restoreGenderOriginalState();
    this.initialize();
  }

  public onGenderSaveClick() {
    this.jobseekerService.saveJobseekerDetails(this.jobseeker).subscribe(() => {
      this.initialize();
      this.onUpdate.emit(this.jobseeker);
    });
  }

  private saveBirthDateOriginalState() {
    this.birthDateOriginalState = {
      day: this.jobseeker && this.jobseeker.birthDay,
      month: this.jobseeker && this.jobseeker.birthMonth,
      year: this.jobseeker && this.jobseeker.birthYear,
    };
  }

  private restoreBirthDateOriginalState() {
    this.jobseeker.birthDay = this.birthDateOriginalState.day;
    this.jobseeker.birthMonth = this.birthDateOriginalState.month;
    this.jobseeker.birthYear = this.birthDateOriginalState.year;
  }

  public onBirthDateEditClick() {
    this.saveBirthDateOriginalState();
    this.onBirthDateChange(); // to adjust birthday dropdowns
    this.isInEditMode = true;
    this.fieldStates.birthDate.isInEditMode = true;
  }

  public onBirthDateEditCancelClick() {
    this.fieldStates.birthDate.isInEditMode = false;
    this.isInEditMode = false;
    this.restoreBirthDateOriginalState();
    this.initialize();
  }

  onBirthDateChange() {
    this.birthDayClassName = '';
    var day = parseInt(this.jobseeker && this.jobseeker.birthDay);
    var month = null;
    var year = null;
    var isLeapYear = null;
    if (this.jobseeker && this.jobseeker.birthDay != null)
      month = parseInt(this.jobseeker && this.jobseeker.birthMonth);
    if (this.jobseeker && this.jobseeker.birthYear != null) {
      year = parseInt(this.jobseeker && this.jobseeker.birthYear);
      isLeapYear = (year % 4 == 0 && year % 100 != 0) || year % 400 == 0;
    }

    // day dropdown change start
    let is30month = month == 4 || month == 6 || month == 9 || month == 11;
    if (is30month) {
      let idx = -1;
      if ((idx = this.dayList.indexOf(31)) > -1) this.dayList.splice(idx, 1);
    } else if (month == 2) {
      let idx = -1;
      if ((idx = this.dayList.indexOf(30)) > -1) this.dayList.splice(idx, 1);
      if ((idx = this.dayList.indexOf(31)) > -1) this.dayList.splice(idx, 1);
      if (!isLeapYear && (idx = this.dayList.indexOf(29)) > -1)
        this.dayList.splice(idx, 1);
      if (isLeapYear && this.dayList.indexOf(29) < 0) this.dayList.push(29);
    } else {
      if (this.dayList.indexOf(29) < 0) this.dayList.push(29);
      if (this.dayList.indexOf(30) < 0) this.dayList.push(30);
      if (this.dayList.indexOf(31) < 0) this.dayList.push(31);
    }
    // day dropdown change end

    if (day == 29 && !isLeapYear && year != null && month === 2) {
      this.jobseeker.birthDay = null;
      this.birthDayClassName = 'birth-day-error';
    }
    if ((day == 31 && is30month) || (day == 30 && month == 2)) {
      this.jobseeker.birthDay = null;
      this.birthDayClassName = 'birth-day-error';
    }
    this.fieldStates.birthDate.isInEditMode = true;
  }

  onBirthDateSaveClick() {
    var validators = this.getBirthDateValidators();
    this.invokeValidation(validators, () => {
      let dateString =
        this.jobseeker.birthYear +
        '/' +
        this.jobseeker.birthMonth +
        '/' +
        this.jobseeker.birthDay;
      this.jobseeker.birthDate = dateString;
      this.jobseekerService
        .saveJobseekerDetails(this.jobseeker)
        .subscribe(() => {
          this.initialize();
          this.onUpdate.emit(this.jobseeker);
        });
    });
  }

  private saveCityOriginalState() {
    this.cityOriginalState = {
      cityId: this.jobseeker && this.jobseeker.cityId,
      cityName: this.jobseeker && this.jobseeker.cityName,
    };
  }

  private restoreCityOriginalState() {
    this.jobseeker.cityId = this.cityOriginalState.cityId;
    this.jobseeker.cityName = this.cityOriginalState.cityName;
  }

  public onCityEditClick() {
    this.saveCityOriginalState();
    this.isInEditMode = true;
    this.fieldStates.city.isInEditMode = true;
  }

  public onCityEditCancelClick() {
    this.fieldStates.city.isInEditMode = false;
    this.isInEditMode = false;
    this.restoreCityOriginalState();
    this.initialize();
  }

  public onCitySaveClick() {
    this.jobseekerService.saveJobseekerDetails(this.jobseeker).subscribe(() => {
      this.initialize();
      this.onUpdate.emit(this.jobseeker);
    });
  }

  private saveAddressOriginalState() {
    this.addressOriginalState = {
      address: this.jobseeker && this.jobseeker.address,
    };
  }

  private restoreAddressOriginalState() {
    this.jobseeker.address = this.addressOriginalState.address;
  }

  public onAddressEditClick() {
    this.saveAddressOriginalState();
    this.isInEditMode = true;
    this.fieldStates.address.isInEditMode = true;
  }

  public onAddressEditCancelClick() {
    this.fieldStates.address.isInEditMode = false;
    this.isInEditMode = false;
    this.restoreAddressOriginalState();
    this.initialize();
  }

  onAddressSaveClick() {
    var validators = this.getAddressValidators();
    this.invokeValidation(validators, () => {
      this.jobseekerService
        .saveJobseekerDetails(this.jobseeker)
        .subscribe(() => {
          this.initialize();
          this.onUpdate.emit(this.jobseeker);
        });
    });
  }

  private saveDrivingLicenseOriginalState() {
    this.drivingLicenseOriginalState = {
      drivingLicenses:
        this.jobseekerInput && this.jobseekerInput.drivingLicenses,
    };
  }

  private restoreDrivingLicenseOriginalState() {
    this.jobseeker.drivingLicenses =
      this.drivingLicenseOriginalState.drivingLicenses;
  }

  public onDrivingLicenseEditClick() {
    this.saveDrivingLicenseOriginalState();
    this.isInEditMode = true;
    this.fieldStates.drivingLicenses.isInEditMode = true;
  }

  public onDrivingLicenseEditCancelClick() {
    this.fieldStates.drivingLicenses.isInEditMode = false;
    this.isInEditMode = false;
    this.drivingLicense = null;
    this.restoreDrivingLicenseOriginalState();
    this.initialize();
  }

  public onDrivingLicenseOptionRemoveClick(drivingLicense) {
    let list = this.jobseeker && this.jobseeker.drivingLicenses;
    let index = list.indexOf(drivingLicense);
    list.splice(index, 1);
  }

  public onDrivingLicenseChange() {
    let drivingLicense = this.drivingLicense;
    let drivingLicenses = this.jobseeker && this.jobseeker.drivingLicenses;

    if (drivingLicense == null) return;

    if (drivingLicenses.some((c) => c.drivingLicenceId == drivingLicense.value))
      return;

    let tmp = new DrivingLicense();
    tmp.drivingLicenceId = drivingLicense.value;
    tmp.name = drivingLicense.text;
    drivingLicenses.push(tmp);
  }

  public onDrivingLicensesSaveClick() {
    this.jobseekerService.saveJobseekerDetails(this.jobseeker).subscribe(() => {
      this.initialize();
      this.onUpdate.emit(this.jobseeker);
    });
  }

  public onPhotoDelete() {
    if (confirm(this.translationService.instant('message.AreYouSure'))) {
      this.jobseeker.photoHref = null;
      this.jobseekerService
        .saveJobseekerDetails(this.jobseeker)
        .subscribe(() => {
          this.initialize();
          this.onUpdate.emit(this.jobseeker);
        });
    }
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  public invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getBirthDateValidators() {
    let field = this.field;
    let jobseeker = this.jobseeker;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );

    return [
      Validator.forRequired(
        field.birthDay,
        () => jobseeker.birthDay,
        emptyMessage
      ),
      Validator.forRequired(
        field.birthMonth,
        () => jobseeker.birthMonth,
        emptyMessage
      ),
      Validator.forRequired(
        field.birthYear,
        () => jobseeker.birthYear,
        emptyMessage
      ),
    ];
  }

  private getAddressValidators() {
    let field = this.field;
    let jobseeker = this.jobseeker;

    let incorrectFormat = this.translationService.instant(
      'message.Minimum 3 Symbols'
    );

    return [
      Validator.forPattern(
        field.address,
        () => jobseeker.address,
        Patterns.jobseekerAddress,
        incorrectFormat
      ),
    ];
  }

  ngOnInit() {
    let config = this.config.getCachedConfig();
    this.dateFormat = config.dateFormat;
  }
}
